import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';

import {
  Observable,
  catchError,
  map,
  of,
  retry,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';

import {
  LOG_MODULE,
  LoggerService,
  extractErrorCode,
  resolveI18nKey,
} from '../../core/logging';
import { AuthService } from '../service/auth.service';
import { AuthStateService } from '../service/auth-state.service';
import { CsrfTokenService } from '../service/csrf.service';
import { NotificationService } from '../service/notification.service';
import { ReturnUrlService } from '../service/return-url.service';
import { SessionExpiryService } from '../service/session-expiry.service';

const UNAUTHORIZED = 401;
const SERVER_ERROR_FLOOR = 500;
const TRANSIENT_RETRY_COUNT = 2;
const TRANSIENT_RETRY_DELAY_MS = 1500;
const SERVER_ERROR_PATH = '/server-error';

function isUnauthorized(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === UNAUTHORIZED;
}

function isTransient(error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    (error.status === 0 || error.status >= SERVER_ERROR_FLOOR)
  );
}

export const AuthGuard: CanActivateFn = (
  _route,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const authService: AuthService = inject(AuthService);
  const authStateService: AuthStateService = inject(AuthStateService);
  const csrfTokenService: CsrfTokenService = inject(CsrfTokenService);
  const sessionExpiry: SessionExpiryService = inject(SessionExpiryService);
  const notification: NotificationService = inject(NotificationService);
  const returnUrl: ReturnUrlService = inject(ReturnUrlService);
  const logger: LoggerService = inject(LoggerService);
  const router: Router = inject(Router);
  const wasAuthenticated = authService.isAuthenticatedSignal();

  return authStateService.ensureLoaded().pipe(
    retry({
      count: TRANSIENT_RETRY_COUNT,
      delay: (error: unknown): Observable<number> =>
        isTransient(error)
          ? timer(TRANSIENT_RETRY_DELAY_MS)
          : throwError(() => error),
    }),
    take(1),
    tap(() => authService.isAuthenticatedSignal.set(true)),
    switchMap(() =>
      csrfTokenService.getCsrfToken().pipe(map((): boolean | UrlTree => true)),
    ),
    catchError((error: unknown) => {
      const status = (error as HttpErrorResponse)?.status;
      const errorCode = extractErrorCode(error);

      if (isUnauthorized(error)) {
        if (wasAuthenticated) {
          sessionExpiry.handle(error, 'guard');

          return of(false);
        }
        authService.isAuthenticatedSignal.set(false);
        returnUrl.remember(state.url);

        return of(router.createUrlTree(['']));
      }
      if (!wasAuthenticated && isTransient(error)) {
        returnUrl.remember(state.url);
        logger.warn('Backend unreachable while loading the session', error, {
          module: LOG_MODULE.authGuard,
          status,
        });

        return of(router.createUrlTree([SERVER_ERROR_PATH]));
      }
      if (!wasAuthenticated) {
        authService.isAuthenticatedSignal.set(false);
        returnUrl.remember(state.url);

        return of(router.createUrlTree(['']));
      }
      logger.warn('Navigation blocked by a transient auth error', error, {
        module: LOG_MODULE.authGuard,
        status,
        errorCode,
      });
      notification.error(resolveI18nKey(errorCode, status));

      return of(false);
    }),
  );
};
